import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { OrderExtensionProps } from "../../pages/OrderExtension/type";

interface RequestDetails {
  Action: string;
  OrderId?: number; // Optional for initial fetch
  OrderSkuId?: number | undefined; // Optional for SKU extension fetch
  shareEstimate?: boolean;
}

interface OrderExtensionState {
  isLoading: boolean;
  data: OrderExtensionProps | null;
  estimateData?: OrderExtensionProps | null; // Optional field for estimate data
  error: string | null;
}

const initialState: OrderExtensionState = {
  isLoading: true,
  data: null,
  estimateData: null, // Initialize estimateData as null
  error: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchOrderData = createAsyncThunk<any, RequestDetails>(
  "OrderExtensionSlice/fetchOrderData",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      OrderId: requestDetails.OrderId, // Use 0 if OrderId is not provided
      ...(requestDetails.OrderSkuId !== undefined && {
        OrderSkuId: requestDetails.OrderSkuId,
      }), // Include OrderSkuId for SKU extension fetch
    };
    try {
      const response = await httpClient.post("/order", requestParam);
      if (response.status != 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: "Failed to get order extension data",
        });
      } else {
        if (
          response?.data?.Message === "404_no_order_found" &&
          response?.data?.Code === 404
        ) {
          return thunkApi.rejectWithValue({
            message: "delete_order",
          });
        } else {
          return response.data;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (error.status === 401) {
        return thunkApi.rejectWithValue({
          message: "access_order",
        });
      } else if (error.status === 404) {
        return thunkApi.rejectWithValue({
          message: "delete_order",
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to get order data",
        });
      }
    }
  },
);

export const fetchEsixtingOrderData = createAsyncThunk<any, RequestDetails>(
  "OrderExtensionSlice/fetchEsixtingOrderData",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      OrderId: requestDetails.OrderId, // Use 0 if OrderId is not provided
    };
    try {
      const response = await httpClient.post("/order", requestParam);
      if (response.status != 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: "Failed to get checkout data",
        });
      } else {
        if (
          response?.data?.Message === "404_no_estimate_found" &&
          response?.data?.Code === 404
        ) {
          return thunkApi.rejectWithValue({
            message: "delete_estimate",
          });
        } else {
          return response.data;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (error.status === 401) {
        return thunkApi.rejectWithValue({
          message: "access_estimate",
        });
      } else if (error.status === 404) {
        return thunkApi.rejectWithValue({
          message: "delete_estimate",
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to get checkout data",
        });
      }
    }
  },
);

export const retriveOrderSkuExtensionConfirmation = createAsyncThunk<
  any,
  RequestDetails
>(
  "OrderExtensionSlice/retriveOrderSkuExtensionConfirmation",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      OrderSkuId: requestDetails.OrderSkuId,
    };
    try {
      const response = await httpClient.post("/order", requestParam);
      if (response.status != 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: "Failed to get sku confirmation data",
        });
      } else {
        if (
          response?.data?.Message === "404_no_estimate_found" &&
          response?.data?.Code === 404
        ) {
          return thunkApi.rejectWithValue({
            message: "Failed to get sku confirmation data",
          });
        } else {
          return response.data;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (error.status === 401) {
        return thunkApi.rejectWithValue({
          message: "access_estimate",
        });
      } else if (error.status === 404) {
        return thunkApi.rejectWithValue({
          message: "delete_estimate",
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to get checkout data",
        });
      }
    }
  },
);

export const retrieveExtensionOrderConfirmation = createAsyncThunk<
  any,
  RequestDetails
>(
  "OrderExtensionSlice/retrieveExtensionOrderConfirmation",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      OrderId: requestDetails.OrderId,
    };
    try {
      const response = await httpClient.post("/order", requestParam);
      if (response.status != 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: "Failed to get sku confirmation data",
        });
      } else {
        if (
          response?.data?.Message === "404_no_estimate_found" &&
          response?.data?.Code === 404
        ) {
          return thunkApi.rejectWithValue({
            message: "Failed to get order confirmation data",
          });
        } else {
          return response.data;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (error.status === 401) {
        return thunkApi.rejectWithValue({
          message: "access_estimate",
        });
      } else if (error.status === 404) {
        return thunkApi.rejectWithValue({
          message: "delete_estimate",
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to get order confirmation data",
        });
      }
    }
  },
);

interface OrderNowRequestDetails {
  Action: string;
  OrderSkuExtensions: {
    OrderSkuId: string;
    AdditionalIntervals: number;
    EngagementId: string | null;
  }[];
  opportunityCode: string;
  isSkuExtension: boolean;
}

export const callOrderExtensionSave = createAsyncThunk<
  any,
  OrderNowRequestDetails
>(
  "OrderExtensionSlice/callOrderExtensionSave",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/order", {
        Action: requestDetails.Action,
        OrderSkuExtensions: requestDetails.OrderSkuExtensions,
        OpportunityId: requestDetails.opportunityCode, // Pass the opportunity code to the API
        IsSkuExtension: requestDetails.isSkuExtension, // Pass the isSkuExtension flag to the API
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to call Order Extension Save",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response?.data?.Status &&
        error.response?.data?.Status == "error"
      ) {
        return thunkApi.rejectWithValue({
          message: error.response.data.Message,
          payload: error.response.data.Data,
        });
      } else {
        console.error("🚀 ~ error:", error);
        return thunkApi.rejectWithValue({
          message: "Failed to call Order Extension Save",
        });
      }
    }
  },
);

const OrderExtensionSlice = createSlice({
  name: "OrderExtensionSlice",
  initialState,
  reducers: {
    resetOrderExtensionSate: (state) => {
      state.isLoading = false;
      state.data = null;
      state.estimateData = null; // Reset estimateData as well
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchOrderData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchOrderData.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action?.payload?.message;
        },
      );
  },
});

export const { resetOrderExtensionSate } = OrderExtensionSlice.actions;

export default OrderExtensionSlice.reducer;
